import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchItems } from "../../store/inventorySlice";
import api from "../../services/api";

import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";

// 1. REUSABLE SEARCH BAR COMPONENT
const SearchBar = ({ value, onChange, placeholder = "Search here..." }) => (
  <div style={styles.searchContainer}>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.searchInput}
    />
  </div>
);

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  success: "#16A34A",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const InventoryList = () => {
  const dispatch = useDispatch();
  const { items = [] } = useSelector((s) => s.inventory || {});

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [units, setUnits] = useState([]);
  const [types, setTypes] = useState([]);

  // 2. SEARCH STATE
  const [searchTerm, setSearchTerm] = useState("");

  const drawerRef = useRef(null);

  const initialForm = {
    name: "",
    product_type_id: "",
    primary_unit_id: "",
    secondary_unit_id: "",
    price: "",
    opening_stock: "",
    min_stock: "",
  };

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    dispatch(fetchItems());
    loadMasters();
  }, [dispatch]);

  const loadMasters = async () => {
    try {
      const [u, t] = await Promise.all([
        api.get("/units/list.php"),
        api.get("/product-types/list.php"),
      ]);
      setUnits(u.data?.data || []);
      setTypes(t.data?.data || []);
    } catch (err) {
      console.error("Master Data Load Error:", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && drawerRef.current && !drawerRef.current.contains(e.target)) {
        resetForm();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError("");
    setForm(initialForm);
  };

  const handleExportExcel = () => {
    const url = "https://codesaarthi.online/khatasaarthi/api/inventory/export.php";
    window.open(url, "_blank");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.product_type_id || !form.primary_unit_id || !form.price) {
      setError("Name, Product Type, Primary Unit, and Price are required.");
      return;
    }

    try {
      const payload = {
        ...form,
        product_type_id: Number(form.product_type_id),
        primary_unit_id: Number(form.primary_unit_id),
        secondary_unit_id: form.secondary_unit_id ? Number(form.secondary_unit_id) : null,
        price: parseFloat(form.price),
        opening_stock: parseFloat(form.opening_stock || 0),
        min_stock: parseFloat(form.min_stock || 0),
      };

      if (editingId) {
        await api.post("/inventory/update.php", { id: editingId, ...payload });
      } else {
        await api.post("/inventory/create.php", payload);
      }
      resetForm();
      dispatch(fetchItems());
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong saving the product.");
    }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name,
      product_type_id: item.product_type_id,
      primary_unit_id: item.primary_unit_id,
      secondary_unit_id: item.secondary_unit_id || "",
      price: item.price,
      opening_stock: item.opening_stock,
      min_stock: item.min_stock,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.post("/inventory/delete.php", { id });
      dispatch(fetchItems());
    } catch {
      alert("Delete failed");
    }
  };

  // 3. FILTER LOGIC
  const filteredItems = items.filter((i) => 
    i.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Inventory Management</h2>
          <p style={styles.subtitle}>Items & Stock Levels</p>
          
          {/* 4. SEARCH BAR PLACEMENT (TITLE KE NEECHE) */}
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search products..." 
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button style={styles.addBtn} onClick={() => setShowForm(true)}>
            + Add Product
          </button>
          <button style={{ ...styles.addBtn, background: COLORS.success }} onClick={handleExportExcel}>
            Export Excel
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Stock</th>
                <th style={styles.th}>Stock Value</th>
                <th style={styles.th}>Min</th>
                <th style={styles.th}>Status</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* 5. MAPPING FILTERED ITEMS */}
              {filteredItems.map((i, index) => {
                const stock = Number(i.current_stock) || 0;
                const price = Number(i.price) || 0;
                return (
                  <tr key={i.id}>
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.tdBold}>{i.name}</td>
                    <td style={styles.td}>₹ {price.toFixed(2)}</td>
                    <td style={styles.td}>{stock}</td>
                    <td style={styles.td}>₹ {(stock * price).toFixed(2)}</td>
                    <td style={styles.td}>{i.min_stock}</td>
                    <td style={styles.td}>
                      {i.low_stock == 1 ? (
                        <span style={styles.lowBadge}>Low Stock</span>
                      ) : (
                        <span style={styles.okBadge}>In Stock</span>
                      )}
                    </td>
                    <td style={styles.actionCell}>
                      <div style={styles.iconWrapper}>
                        <button style={styles.iconBtn} onClick={() => handleEdit(i)}>
                          <img src={editIcon} alt="edit" style={styles.icon} />
                        </button>
                        <button style={styles.iconBtn} onClick={() => handleDelete(i.id)}>
                          <img src={deleteIcon} alt="delete" style={styles.icon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* PORTAL FOR DRAWER */}
      {showForm && createPortal(
        <div style={styles.overlay}>
          <div ref={drawerRef} style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <h2 style={styles.drawerTitle}>{editingId ? "Edit Product" : "Add Product"}</h2>
              <button style={styles.closeBtn} onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={submit}>
              <div style={styles.grid}>
                <Input label="Product Name" name="name" value={form.name} setForm={setForm} />
                <Select label="Product Type" name="product_type_id" value={form.product_type_id} options={types} setForm={setForm} />
                <Select label="Primary Unit" name="primary_unit_id" value={form.primary_unit_id} options={units} setForm={setForm} />
                <Select label="Secondary Unit (Optional)" name="secondary_unit_id" value={form.secondary_unit_id} options={units} setForm={setForm} isOptional />
                <Input label="Price (₹)" name="price" type="number" value={form.price} setForm={setForm} />
                <Input label="Opening Stock" name="opening_stock" type="number" value={form.opening_stock} setForm={setForm} />
                <Input label="Minimum Stock Alert" name="min_stock" type="number" value={form.min_stock} setForm={setForm} />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.actionRow}>
                <button type="button" style={styles.cancelBtn} onClick={resetForm}>Cancel</button>
                <button type="submit" style={styles.primaryBtn}>
                  {editingId ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* REUSABLE INPUT COMPONENTS */

const Input = ({ label, name, value, setForm, type = "text" }) => (
  <div style={styles.field}>
    <label style={styles.label}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
      style={styles.input}
    />
  </div>
);

const Select = ({ label, name, value, options, setForm, isOptional = false }) => (
  <div style={styles.field}>
    <label style={styles.label}>{label}</label>
    <select
      value={value}
      onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
      style={styles.input}
      required={!isOptional}
    >
      <option value="">{isOptional ? "None" : "Select"}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  </div>
);

const styles = {
  // New Search Styles
  searchContainer: { marginTop: "12px", width: "300px" },
  searchInput: { 
    width: "100%", 
    padding: "8px 12px", 
    borderRadius: "8px", 
    border: `1px solid ${COLORS.border}`, 
    fontSize: "14px", 
    outline: "none" 
  },

  page: { background: COLORS.bg, padding: "10px clamp(15px,4vw,30px) 30px", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 25 },
  title: { fontSize: 28, fontWeight: 700, margin: 0 },
  subtitle: { color: COLORS.muted, margin: 0 },
  addBtn: { background: COLORS.primary, color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 600, cursor: "pointer", height: 42 },
  card: { background: COLORS.card, borderRadius: 18, padding: "25px 30px" },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850 },
  th: { padding: "14px 12px", textAlign: "left", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  thCenter: { padding: "14px 12px", textAlign: "center", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "16px 12px", borderBottom: `1px solid ${COLORS.border}` },
  tdBold: { padding: "16px 12px", fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` },
  actionCell: { borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" },
  iconWrapper: { display: "flex", justifyContent: "center", gap: 16 },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer", padding: 4 },
  icon: { width: 18, height: 18 },
  lowBadge: { background: "#FFEBEE", color: COLORS.danger, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  okBadge: { background: "#E3F2FD", color: COLORS.primary, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 9999 },
  drawer: { width: "min(600px, 100%)", height: "100vh", background: "#fff", padding: "25px 30px 100px", overflowY: "auto", boxShadow: "-6px 0 20px rgba(0,0,0,0.15)", boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 25 },
  drawerTitle: { fontSize: 22, fontWeight: 700, margin: 0 },
  closeBtn: { border: "none", background: "none", fontSize: 24, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "1fr", gap: 18 },
  field: { display: "flex", flexDirection: "column" },
  label: { marginBottom: 6, color: COLORS.muted, fontWeight: 500 },
  input: { width: "100%", padding: "12px", borderRadius: 6, border: `1px solid ${COLORS.border}`, boxSizing: "border-box", fontSize: "14px" },
  actionRow: { display: "flex", justifyContent: "flex-end", marginTop: 35, gap: 12 },
  cancelBtn: { padding: "10px 20px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer" },
  primaryBtn: { padding: "10px 25px", borderRadius: 6, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 600, cursor: "pointer" },
  error: { marginTop: 12, color: COLORS.danger, fontSize: "14px" },
};

export default InventoryList;